// Pricing and sizing logic per user's specification.

const GST_PERCENT = 5;

// Base price tables (before GST) and logistics per capacity, roof, and phase variants where applicable.
// Note: Installation fees now have 5% GST added (not inclusive)
// DCR (subsidy panels) – only 2–5 kW provided.
const DCR = {
  rcc: {
    elevated: {
      2: { base: 169400, logistics: 20000 },
      3: { base: 178000, logistics: 26000 },
      4: { base: 246400, logistics: 30200 },
      "5-single": { base: 283800, logistics: 37500 },
      "5-three": { base: 332200, logistics: 38500 },
    },
    portrait: {
      2: { base: 127600, logistics: 20000 },
      3: { base: 172700, logistics: 26000 },
      4: { base: 233200, logistics: 30200 },
      "5-single": { base: 275000, logistics: 37500 },
      "5-three": { base: 323400, logistics: 38500 },
    },
  },
  sheet: {
    2: { base: 118800, logistics: 18000 },
    3: { base: 160600, logistics: 24000 },
    4: { base: 216700, logistics: 29200 },
    "5-single": { base: 257400, logistics: 35500 },
    "5-three": { base: 304700, logistics: 36500 },
  },
};

// NON-DCR (non-subsidy panels)
const NON_DCR = {
  rcc: {
    elevated: {
      2: { base: 112200, logistics: 20000 },
      3: { base: 137000, logistics: 26000 },
      4: { base: 191740, logistics: 30200 },
      "5-single": { base: 219520, logistics: 37500 },
      "5-three": { base: 259840, logistics: 38500 },
      6: { base: 295680, logistics: 40000 },
      7: { base: 331520, logistics: 44000 },
      8: { base: 362880, logistics: 49000 },
      9: { base: 395360, logistics: 55000 },
      10: { base: 422240, logistics: 57000 },
      11: { base: 477120, logistics: 60000 },
      15: { base: 588000, logistics: 65000 },
      20: { base: 827680, logistics: 85000 },
      25: { base: 992320, logistics: 105000 },
      30: { base: 1134560, logistics: 126000 },
      35: { base: 1327200, logistics: 146000 },
      40: { base: 1469440, logistics: 160000 },
      45: { base: 1640800, logistics: 171450 },
      50: { base: 1786960, logistics: 190500 },
      60: { base: 2154880, logistics: 205350 },
      70: { base: 2457280, logistics: 239575 },
      80: { base: 2722720, logistics: 259200 },
      90: { base: 3133760, logistics: 291600 },
      100: { base: 3401440, logistics: 316840 },
    },
    portrait: {
      2: { base: 102300, logistics: 20000 },
      3: { base: 132000, logistics: 26000 },
      4: { base: 177100, logistics: 30200 },
      "5-single": { base: 206800, logistics: 37500 },
      "5-three": { base: 246400, logistics: 38500 },
      6: { base: 280500, logistics: 40000 },
      7: { base: 306900, logistics: 44000 },
      8: { base: 342100, logistics: 49000 },
      9: { base: 370700, logistics: 55000 },
      10: { base: 396000, logistics: 57000 },
      11: { base: 458080, logistics: 60000 },
      15: { base: 561120, logistics: 65000 },
      20: { base: 791840, logistics: 85000 },
      25: { base: 947520, logistics: 105000 },
      30: { base: 1084160, logistics: 126000 },
      35: { base: 1256640, logistics: 146000 },
      40: { base: 1401120, logistics: 160000 },
      45: { base: 1562400, logistics: 171450 },
      50: { base: 1694560, logistics: 190500 },
      60: { base: 2045120, logistics: 205350 },
      70: { base: 2338560, logistics: 239575 },
      80: { base: 2587200, logistics: 259200 },
      90: { base: 2980320, logistics: 291600 },
      100: { base: 3230080, logistics: 316840 },
    },
  },
  sheet: {
    2: { base: 95700, logistics: 20000 },
    3: { base: 122100, logistics: 26000 },
    4: { base: 165760, logistics: 30200 },
    "5-single": { base: 189200, logistics: 37500 },
    "5-three": { base: 228800, logistics: 38500 },
    6: { base: 256300, logistics: 40000 },
    7: { base: 279400, logistics: 44000 },
    8: { base: 312600, logistics: 49000 },
    9: { base: 338800, logistics: 55000 },
    10: { base: 360800, logistics: 57000 },
    11: { base: 432320, logistics: 60000 },
    15: { base: 533120, logistics: 65000 },
    20: { base: 750400, logistics: 90000 },
    25: { base: 894880, logistics: 100000 },
    30: { base: 1022560, logistics: 120500 },
    35: { base: 1183840, logistics: 132500 },
    40: { base: 1316000, logistics: 150000 },
    45: { base: 1469440, logistics: 168500 },
    50: { base: 1592640, logistics: 190500 },
    60: { base: 1935360, logistics: 215350 },
    70: { base: 2203040, logistics: 235575 },
    80: { base: 2429280, logistics: 286200 },
    90: { base: 2788800, logistics: 291600 },
    100: { base: 2996000, logistics: 310840 },
  },
};

function roundUpToAllowed(capacityKw, roof, phase, panelType, structureType) {
  // For systems above 10kW, use commercial pricing (multiples of 5kW)
  if (capacityKw > 10) {
    // For RCC roof, we need structure type
    if (roof === "rcc") {
      const rccTable = NON_DCR.rcc[structureType || "elevated"];
      const commercialSizes = Object.keys(rccTable)
        .filter((k) => /^\d+$/.test(k))
        .map((k) => parseInt(k, 10))
        .filter((k) => k > 10)
        .sort((a, b) => a - b);
      const cap = Math.round(capacityKw);
      // Find the nearest size that is >= capacity (round up)
      for (const size of commercialSizes) {
        if (size >= cap) {
          return String(size);
        }
      }
      // If capacity exceeds max (100kW), return max
      return "100";
    } else {
      // Sheet roof - use NON_DCR.sheet
      const sheetTable = NON_DCR.sheet;
      const commercialSizes = Object.keys(sheetTable)
        .filter((k) => /^\d+$/.test(k))
        .map((k) => parseInt(k, 10))
        .filter((k) => k > 10)
        .sort((a, b) => a - b);
      const cap = Math.round(capacityKw);
      for (const size of commercialSizes) {
        if (size >= cap) {
          return String(size);
        }
      }
      return "100";
    }
  }
  
  // Get the appropriate table based on roof and structure type
  let table;
  if (roof === "rcc") {
    const rccTable = panelType === "DCR" ? DCR.rcc : NON_DCR.rcc;
    table = rccTable[structureType || "elevated"];
  } else {
    table = panelType === "DCR" ? DCR.sheet : NON_DCR.sheet;
  }
  
  const keys = Object.keys(table);
  const numericSizes = keys
    .filter((k) => /^\d+$/.test(k))
    .map((k) => parseInt(k, 10))
    .sort((a, b) => a - b);
  // Handle 5kW phase split
  if (Math.round(capacityKw) === 5) {
    return phase === "single" ? "5-single" : "5-three";
  }
  // Choose nearest not exceeding; minimum 2 kW
  const cap = Math.max(2, Math.round(capacityKw));
  let pick = numericSizes[0];
  for (const n of numericSizes) {
    if (n <= cap) pick = n;
  }
  return String(pick);
}

export function computeCapacityKw({ avgMonthlyUnits, requestedCapacityKw, sanctionedLoadKw }) {
  if (requestedCapacityKw && requestedCapacityKw > 0) return requestedCapacityKw;
  // If no units are provided, fall back directly to sanctioned load.
  if (!avgMonthlyUnits && sanctionedLoadKw) {
    return sanctionedLoadKw;
  }
  const est = Math.ceil((avgMonthlyUnits || 0) / 120); // 1kW ~ 120 units/month
  return Math.min(est || 2, sanctionedLoadKw || est || 2);
}

export function decidePanelType({ connection, wantsSubsidy, panelOverride }) {
  if (panelOverride === "NON-DCR") return "NON-DCR";
  if (connection === "residential" && wantsSubsidy) return "DCR";
  return "NON-DCR";
}

export function computeSubsidy({ connection, capacityKw, panelType }) {
  // Subsidy only for residential DCR systems up to 5kW
  if (connection !== "residential" || panelType === "NON-DCR") {
    return { applicable: false, amount: 0 };
  }
  if (capacityKw <= 0) return { applicable: false, amount: 0 };
  // Strong rule: Subsidy NOT applicable for residential > 5kW
  if (capacityKw > 5) {
    return { applicable: false, amount: 0 };
  }
  let amount = 0;
  if (capacityKw <= 2) {
    amount = 30000 * capacityKw;
  } else if (capacityKw <= 3) {
    amount = 30000 * 2 + 18000 * (capacityKw - 2);
  } else {
    amount = 78000; // cap for >3 kW (but only up to 5kW)
  }
  return { applicable: true, amount: Math.round(amount) };
}

export function priceQuote(inputs) {
  const {
    roof, // 'rcc' | 'sheet'
    phase, // 'single' | 'three'
    connection, // 'residential' | 'commercial'
    wantsSubsidy,
    requestedCapacityKw,
    sanctionedLoadKw,
    avgMonthlyUnits,
    panelOverride, // 'NON-DCR' | undefined
    structureType, // 'elevated' | 'portrait' (required for RCC roof)
    customBasePrice, // Admin-only: Custom base price override (pre-GST)
  } = inputs;

  const capacityKw = computeCapacityKw({ avgMonthlyUnits, requestedCapacityKw, sanctionedLoadKw });
  let panelType = decidePanelType({ connection, wantsSubsidy, panelOverride });

  // For RCC roof, structure type is required
  if (roof === "rcc" && !structureType) {
    structureType = "elevated"; // Default to elevated if not specified
  }

  // Strong rule: Systems above 10kW are always commercial and NON-DCR
  if (capacityKw > 10) {
    // Force commercial connection and NON-DCR for systems above 10kW
    panelType = "NON-DCR";
    
    // Check if capacity is a multiple of 5kW and exists in pricing table
    const roundedCapacity = Math.round(capacityKw);
    const key = String(roundedCapacity);
    
    // Get pricing table based on roof type and structure type
    let priceEntry;
    if (roof === "rcc") {
      const rccTable = NON_DCR.rcc[structureType || "elevated"];
      if (!rccTable || !rccTable[key]) {
        throw new Error("CAPACITY_NOT_IN_PRICING_TABLE");
      }
      priceEntry = rccTable[key];
    } else {
      const sheetTable = NON_DCR.sheet;
      if (!sheetTable || !sheetTable[key]) {
        throw new Error("CAPACITY_NOT_IN_PRICING_TABLE");
      }
      priceEntry = sheetTable[key];
    }
    
    // Use custom base price if provided (admin override), otherwise use lookup table price
    const originalBasePrice = priceEntry.base;
    const basePrice = customBasePrice && customBasePrice > 0 ? customBasePrice : originalBasePrice;
    const logisticsBase = priceEntry.logistics;
    // Calculate GST: First add basePrice + logisticsBase to get total system price
    // Then calculate 5% GST on 70% of total system price and 18% GST on 30% of total system price
    const systemTotal = basePrice + logisticsBase;
    const systemTotal70Percent = systemTotal * 0.7;
    const systemTotal30Percent = systemTotal * 0.3;
    const gst5Percent = Math.round((5 / 100) * systemTotal70Percent);
    const gst18Percent = Math.round((18 / 100) * systemTotal30Percent);
    const gstAmount = gst5Percent + gst18Percent;
    // Total is sum of all line items: basePrice + logisticsBase + gst5Percent + gst18Percent
    const totalBeforeSubsidy = basePrice + logisticsBase + gst5Percent + gst18Percent;
    
    const result = {
      capacityKw: roundedCapacity,
      capacityKey: key,
      panelType,
      basePrice,
      gstPercent: GST_PERCENT, // Keep for backward compatibility, but actual GST is split
      gstAmount,
      gst5Percent: gst5Percent, // GST at 5% on 70% of system price
      gst18Percent: gst18Percent, // GST at 18% on 30% of system price
      logisticsBase: logisticsBase,
      logisticsGst: 0, // No separate GST on logistics, it's included in total GST
      logisticsInclusive: logisticsBase, // Show logistics without GST
      totalBeforeSubsidy,
      subsidyApplicable: false,
      subsidyAmount: 0,
      totalPayable: totalBeforeSubsidy,
      isCommercialAbove10kW: true,
      customBasePriceUsed: !!(customBasePrice && customBasePrice > 0), // Flag to indicate custom price was used (always boolean)
    };
    // Only include originalBasePrice if custom price was used (Firebase doesn't allow undefined)
    if (customBasePrice && customBasePrice > 0) {
      result.originalBasePrice = originalBasePrice;
    }
    // Only include structureType if roof is RCC (Firebase doesn't accept undefined)
    if (roof === "rcc") {
      result.structureType = structureType || "elevated";
    }
    return result;
  }

  // Strong rule: If capacity > 5kW and user wants subsidy/DCR, force NON-DCR pricing
  // (DCR pricing only exists up to 5kW, and subsidy not applicable above 5kW)
  if (capacityKw > 5 && panelType === "DCR") {
    panelType = "NON-DCR";
  }

  const key = roundUpToAllowed(capacityKw, roof, phase, panelType, structureType);
  
  // Get the appropriate table based on roof and structure type
  let table;
  if (roof === "rcc") {
    const rccTable = panelType === "DCR" ? DCR.rcc : NON_DCR.rcc;
    table = rccTable[structureType || "elevated"];
  } else {
    table = panelType === "DCR" ? DCR.sheet : NON_DCR.sheet;
  }
  
  const priceEntry = table[key];
  if (!priceEntry) {
    throw new Error("No price available for the selected configuration.");
  }
  // Use custom base price if provided (admin override), otherwise use lookup table price
  const originalBasePrice = priceEntry.base;
  const basePrice = customBasePrice && customBasePrice > 0 ? customBasePrice : originalBasePrice;
  const logisticsBase = priceEntry.logistics;
  // Calculate GST: First add basePrice + logisticsBase to get total system price
  // Then calculate 5% GST on 70% of total system price and 18% GST on 30% of total system price
  const systemTotal = basePrice + logisticsBase;
  const systemTotal70Percent = systemTotal * 0.7;
  const systemTotal30Percent = systemTotal * 0.3;
  const gst5Percent = Math.round((5 / 100) * systemTotal70Percent);
  const gst18Percent = Math.round((18 / 100) * systemTotal30Percent);
  const gstAmount = gst5Percent + gst18Percent;
  const totalBeforeSubsidy = systemTotal + gstAmount;
  const subsidy = computeSubsidy({ connection, capacityKw, panelType });

  const result = {
    capacityKw: Math.round(capacityKw),
    capacityKey: key,
    panelType,
    basePrice,
    gstPercent: GST_PERCENT, // Keep for backward compatibility, but actual GST is split
    gstAmount,
    gst5Percent: gst5Percent, // GST at 5% on 70% of system price
    gst18Percent: gst18Percent, // GST at 18% on 30% of system price
    logisticsBase: logisticsBase,
    logisticsGst: 0, // No separate GST on logistics, it's included in total GST
    logisticsInclusive: logisticsBase, // Show logistics without GST
    totalBeforeSubsidy,
    subsidyApplicable: subsidy.applicable,
    subsidyAmount: subsidy.amount,
    totalPayable: totalBeforeSubsidy, // subsidy not deducted
    isCommercialAbove10kW: false,
    customBasePriceUsed: !!(customBasePrice && customBasePrice > 0), // Flag to indicate custom price was used (always boolean)
  };
  // Only include originalBasePrice if custom price was used (Firebase doesn't allow undefined)
  if (customBasePrice && customBasePrice > 0) {
    result.originalBasePrice = originalBasePrice;
  }
  // Only include structureType if roof is RCC (Firebase doesn't accept undefined)
  if (roof === "rcc") {
    result.structureType = structureType || "elevated";
  }
  return result;
}


